import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { quizMeta, type QuizQuestion } from '../constants/quizQuestions';

/**
 * Merge per-language quiz text (loaded via i18next from
 * `public/locales/<lang>/onboarding.json`) with the role-keyed static
 * metadata (`quizMeta` in `src/constants/quizQuestions.ts`).
 *
 * The hook returns the same shape the OnboardingQuiz component has always
 * consumed: `{ id, question, options[], correct, emoji }[]`. The only
 * difference is `question` + `options` come from the locale bundle now,
 * so a Spanish reader sees Spanish copy without the component knowing.
 *
 * The `correct` index references the position inside the translated
 * `options[]` array — translators MUST keep option ordering identical
 * across languages or the answer key drifts. The `_meta` comment in
 * onboarding.json carries this contract.
 */
export function useQuizQuestions(role: 'chef'): QuizQuestion[] {
  const { t } = useTranslation('onboarding');
  return useMemo(
    () =>
      quizMeta[role].map((meta) => ({
        ...meta,
        question: t(`quiz.${role}.q${meta.id}.question`),
        options: t(`quiz.${role}.q${meta.id}.options`, {
          returnObjects: true,
        }) as string[],
      })),
    [role, t],
  );
}
